import { canonicalWidgetOrigin } from "./tixologiWidgetOrigin";

/**
 * The Tixologi event id carried by a ticket link, or null when the link is not one we built.
 *
 * WRITE PATH. The result lands in `shows.tixologi_event_id` via
 * `@punchup/data/legacy/tour-dates/updaterFunction` and three dashboard forms, so what this accepts
 * is what gets written to the database. Two deliberate non-changes follow from that:
 *
 * 1. It keeps `startsWith`, and is NOT routed through `isTixologiWidgetLink()`. The predicate
 *    answers "is this link on a Tixologi widget HOST?" — which is broader (localhost, the
 *    `NEXT_PUBLIC_TIXOLOGI_URL` host, any subdomain of either). This function answers the narrower
 *    "did THIS repo build this link, so is the `/event/<id>/` segment ours to parse?", and the only
 *    honest test of that is the prefix the link builders emit. Only the VALUE is centralised
 *    (`canonicalWidgetOrigin()`), not the comparison.
 *
 * 2. A link that matches the prefix but carries no `/event/` segment still yields `undefined`, not
 *    `null`. The distinction is load-bearing at the call sites: `undefined` is dropped from a
 *    Supabase update payload (the column keeps its current value) where `null` CLEARS the column.
 *    Do not "tidy" this with `?? null` — that would start nulling ids on links it cannot parse.
 *    The declared return type says `string | null` anyway, and that is a KNOWN GAP, not an
 *    oversight: `noUncheckedIndexedAccess` is off so the compiler reads the indexed access as
 *    `string`, and widening the signature to include `undefined` breaks
 *    `packages/data/src/legacy/tour-dates/updaterFunction.ts`, whose request body is
 *    `satisfies Omit<ShowInfoRow, "created_at">` — a generated row type where the column is
 *    required. Closing the gap honestly means deriving an "update" request shape there first.
 *
 * The one behaviour that did change: an unset or blank `NEXT_PUBLIC_TIXOLOGI_WIDGET_URL` now yields
 * no id at all. It previously interpolated the literal string `"undefined"` when unset (fail
 * closed), but a variable configured as `""` made `startsWith("")` true for every link and wrote an
 * id parsed out of any third-party URL (fail open). Both now resolve to "no configured origin, no
 * id" — strictly narrower, and it never widens what is written.
 */
export function extractTixologiEventId(ticketLink: string): string | null {
  const widgetOrigin = canonicalWidgetOrigin();

  return widgetOrigin && ticketLink && ticketLink.startsWith(widgetOrigin)
    ? ticketLink.split("/event/")[1]?.split("/")[0]
    : null;
}
